import { Component, NgZone, OnInit } from '@angular/core';
import { WebPlayerSharedService } from './core/services/web-player-shared.service';
import { ActivatedRoute, Params, Router } from '@angular/router';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false
})
export class AppComponent implements OnInit {

  validating = false;
  validationError = false;
  validationMessage = "";
  validationDevices = false;
  validationFooterMessage = "";
  showFooterMessage = false;
  showProgressBar: boolean;
  lastCodeUsed: any = null;
  showInput: boolean = false;
  routeSubscription: any;
  paramsSubscription: any;
  fullScreenSize: boolean = false;

  constructor(
    private webPlayerSharedService: WebPlayerSharedService,
    private activatedRoute: ActivatedRoute,
    private ngZone: NgZone,
    private router: Router
  ) {
    // AppComponent is both the bootstrap root and the routed component for
    // /activate and /activate2 (see app-routing.module). Only the ROUTED
    // instance runs the activation flow; the root instance is just the shell
    // hosting the router-outlet. Without this guard both instances run the
    // flow and every open activates (and registers a license open) twice.
    if (!this.activatedRoute.snapshot.routeConfig) {
      this.validating = false;
      return;
    }

    // Check if we're on a /watch/ route - if so, skip validation and let router handle it
    const currentPath = window.location.pathname;
    if (currentPath.includes('/watch/')) {
      // We're on the web player route, don't show validation screen
      this.validating = false;
      return;
    }

    // Check if we're on /activate2/:accessCode route (TV Now app flow)
    if (currentPath.includes('/activate2/')) {
      this.handleActivate2Flow();
      return;
    }

    // Original /activate flow (email flow)
    this.routeSubscription = this.activatedRoute.queryParams.subscribe({
      next: (params: Params) => {
        // Strip whitespace/newlines that email clients may inject when the
        // activation URL is copied from the message body (e.g. Outlook wraps
        // long links) — the token itself never contains whitespace.
        let code = params["code"]?.replace(/\s+/g, "");
        let errorParam = params["error"];
        this.initParams(params);

        // Handle error redirects from web-player (PIN verification errors)
        if (errorParam) {
          this.showErrorFromCode(errorParam);
          return;
        }

        if (this.lastCodeUsed != code + "" && code) {
          this.lastCodeUsed = code + "";
          this.getToken(code + "");
        } else {
          this.getToken(null);
        }
      },
      error: (error) => {
        this.showError();
      }
    })
  }

  ngOnInit(): void {
  }

  /**
   * Handle /activate2/:accessCode route (TV Now app flow)
   * Extracts accessCode from URL and calls API to get token and uri
   */
  handleActivate2Flow() {
    this.validating = true;
    this.showProgressBar = true;
    this.validationMessage = "Authenticating...";
    this.showInput = false;

    // Extract accessCode from URL path (format: /activate2/:accessCode)
    const currentPath = window.location.pathname;
    const pathParts = currentPath.split('/activate2/');
    const accessCode = pathParts.length > 1 ? decodeURIComponent(pathParts[1]) : null;

    if (!accessCode) {
      this.showErrorFromCode("missing_credentials");
      return;
    }

    // Call the activate API with the accessCode
    this.webPlayerSharedService.activateDevice(accessCode).subscribe({
      next: (data: any) => {
        this.showInput = false;
        this.validationDevices = true;
        this.showFooterMessage = false;
        this.validationError = false;
        this.showProgressBar = true;
        this.validationMessage = "Redirecting...";

        // Save token and uri for web player
        this.webPlayerSharedService.saveToken(data.token);
        this.webPlayerSharedService.saveUri(data.uri);

        this.closeVaidation(data.uri);
      },
      error: (error) => {
        this.showProgressBar = false;
        const errorCode = error?.error?.code || error?.code || 'verification_failed';
        this.showErrorFromCode(errorCode);
      }
    });
  }

  initParams(params: Params) {
    let urlQueryParams = this.getQueryParamsFromOldRoute();
    if (params["fullScreen"] || urlQueryParams["fullScreen"]) {
      this.fullScreenSize = params["fullScreen"] === 'true' || urlQueryParams["fullScreen"] === 'true' ? true : false;
      this.saveConfig(this.fullScreenSize);
    } else {
      let config = this.webPlayerSharedService.getPlayerConfig();
      this.fullScreenSize = config?.fullScreenSize || false;
    }
  }

  getToken(code: string) {

    this.validating = true;
    this.showProgressBar = true;

    const oldCode = this.webPlayerSharedService.getCode();

    if ((code && (code !== oldCode || !this.webPlayerSharedService.getToken()))) {
      this.validationMessage = "Authenticating Your Device...";
      this.showInput = false;
      this.webPlayerSharedService.activateDevice(code).subscribe({
        next: (data: any) => {
          this.showInput = false;
          this.validationDevices = true;
          this.showFooterMessage = false; //TODO true if need to show available devices
          this.validationError = false;
          this.showProgressBar = true;
          this.validationMessage = "Redirecting...";
          this.validationFooterMessage = "You have " + data.devices + " more available devices";
          this.webPlayerSharedService.saveCode(code);
          this.webPlayerSharedService.saveToken(data.token);
          this.webPlayerSharedService.saveUri(data.uri);
          this.routeSubscription.unsubscribe();
          this.closeVaidation(data.uri);
        },
        error: (error) => {
          this.showProgressBar = false;
          this.validationMessage = "Code not valid, or no more devices available.";
          this.validationError = false; // Don't show secondary error message
          this.showFooterMessage = false;
          this.showInput = true;
        }
      });
    } else {
      let uri = this.webPlayerSharedService.getUri();
      if (this.webPlayerSharedService.getToken() && uri) {
        this.showProgressBar = true;
        this.showFooterMessage = true;
        this.validationFooterMessage = "Device activated";
        this.validationMessage = "Redirecting...";
        this.showInput = false;
        this.closeVaidation(uri);
      } else if (!code) {
        this.showError();
      }

    }
  }

  activateCode(code: string) {
    this.getToken(code + "");
  }

  showError() {
    this.validating = true;
    this.showProgressBar = false;
    this.validationMessage = "Invalid code.";
    this.validationError = false; // Don't show secondary error message
    this.showFooterMessage = false;
    this.showInput = true;
  }

  /**
   * Show error message based on error code from PIN verification
   */
  showErrorFromCode(errorCode: string) {
    this.validating = true;
    this.showProgressBar = false;
    this.validationError = false; // Don't show secondary error message
    this.showFooterMessage = false;
    this.showInput = false; // Don't show input - they need to use TV Now app

    const errorMessages: { [key: string]: string } = {
      'PIN_INVALID': 'Invalid PIN. Please reopen from the TV Now app.',
      'PIN_MISMATCH': 'PIN does not match this configuration. Please reopen from the TV Now app.',
      'PIN_EXPIRED': 'This PIN has expired. Please request a new activation code.',
      'ACCESS_CODE_EXPIRED': 'Access code has expired. Please click "Watch Now" again in the TV Now app.',
      'missing_credentials': 'Missing authentication. Please reopen from the TV Now app.',
      'verification_failed': 'Verification failed. Please reopen from the TV Now app.',
      'NO_LICENSE_AVAILABLE': 'No TV Now licenses available for this account. Please contact your administrator.',
      'LICENSE_EXPIRED': 'Your TV Now license has expired and no replacement is available. Please contact your administrator.',
    };

    this.validationMessage = errorMessages[errorCode] || 'Access denied. Please reopen from the TV Now app.';
  }

  closeVaidation(uri: string) {
    this.ngZone.run(() => {
      setTimeout(() => {
        const urlDecoded = decodeURIComponent(window.location.pathname);
        const queryParams = urlDecoded.split('?');
        let query = '';
        if (urlDecoded.includes('watch')) {
          query += '?';
          if (queryParams && queryParams.length > 1 && queryParams[1]) {
            query += queryParams[1]
          }
        } else {
          query = '?fullScreen=' + this.fullScreenSize;
        }

        this.validating = false;
        this.router.navigate(['/watch', uri + query]);
      }, 2000);
    });
  }

  getQueryParamsFromOldRoute() {
    const urlDecoded = decodeURIComponent(window.location.pathname);
    const queryParams = urlDecoded.split('?');
    const values = {};
    if (queryParams && queryParams.length > 1) {
      const queryValues = queryParams[1];
      const query = queryValues.split('&');
      for (let i = 0; i < query.length; i++) {
        const element = query[i];
        const keyValue = element.split('=');
        values[keyValue[0]] = encodeURIComponent(keyValue[1]);
      }
    }
    return values;
  }

  saveConfig(fullScreenSize) {
    let config = this.webPlayerSharedService.getPlayerConfig();
    if (!config) {
      config = {}
    }
    config.fullScreenSize = fullScreenSize;
    this.webPlayerSharedService.savePlayerConfig(config);
  }
}
